import { Component, OnInit, OnDestroy } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { HttpClient } from '@angular/common/http';
import { ActivatedRoute, Router, NavigationEnd } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';

import { EditUserVm } from '../../../shared/generated/Administration/Models/User/CreateEditUser/EditUserVm';
import { SystemMessageService, SystemMessage } from '../../../core/system-message/system-message-service';
import { NavigationService } from '../../../shared/navigation/navigation.service';
import { SecurityService } from '../../../core/security/security.service';
import { DynamicSecurity } from '../../../core/security/generated/DynamicSecurity';
import { UserChangePasswordModalComponent } from './user-change-password-modal/user-change-password-modal.component';

@Component({
    selector: 'pcg-edit-user',
    templateUrl: './edit-user.component.html',
    styleUrls: ['./edit-user.component.scss'],
    standalone: false
})
export class EditUserComponent implements OnInit, OnDestroy {
	id: number;
	isLockedOut = false;
	origInactivityTimeout = false;
	subscriptions = new Subscription();
	formGroup = EditUserVm.Form;

	// Security variables
	canEdit = false;
	canCreate = false;
	canViewLoginHistory = false;
	canUnlock = false;
	canResetPassword = false;
	canSendWelcomeEmail = false;
	canViewReports = false;
	canSeePasswordButtons = false;
	isCurrentUser = false;
	openWindow = o => {
		this.router.navigateByUrl('/' + o);
	};

	constructor(
		private route: ActivatedRoute,
		private httpClient: HttpClient,
		public router: Router,
		private modalService: NgbModal,
		private ms: SystemMessageService,
		private nav: NavigationService,
		title: Title,
		private sec: SecurityService
	) {
		title.setTitle('Edit User');
	}

	ngOnInit() {
		this.addSecuritySubscription();
		this.addRouteSubscription();
	}

	/** Update the local dynamic security object when the security updates */
	addSecuritySubscription() {
		this.subscriptions.add(
			this.sec.security$.subscribe((sec: DynamicSecurity) => {
				this.sec.setControlAccess(
					this.formGroup.controls['userProfileIds'],
					'securityAdministration.changeUserSecurityProfiles'
				);
				this.sec.setControlAccess(
					this.formGroup.controls['isActive'],
					'securityAdministration.changeUserActiveStatus'
				);
				this.sec.setControlAccess(this.formGroup.controls['canChangeSecurity'], 'canChangeSecurity');
				this.canEdit = this.sec.hasAccess('securityAdministration.editUserDetails');
				this.canCreate = this.sec.hasAccess('securityAdministration.createUser');
				this.canViewLoginHistory = this.sec.hasAccess('securityAdministration.loginHistory');
				this.canUnlock = this.sec.hasAccess('securityAdministration.unlockAccount');
				this.canResetPassword = this.sec.hasAccess('securityAdministration.resetPassword');
				this.canSendWelcomeEmail = this.sec.hasAccess('securityAdministration.sendWelcomeEmail');
				this.canViewReports = this.sec.hasAccess('securityAdministration.viewSecurityReports');
			})
		);
	}

	/** Subscribe to route to pick up id changes and update page accordingly */
	addRouteSubscription() {
		this.subscriptions.add(
			this.route.paramMap.subscribe(map => {
				// Allow user to view page if it is either their profile
				// or they have admin access to view other people's profiles
				const user = this.sec.getUser();
				this.id = parseInt(map.get('id'), 10);
				if ((this.id !== user.id && !this.canEdit) || (this.id === 0 && !this.canCreate)) {
					this.router.navigate(['/']);
				}
				this.isCurrentUser = this.id === this.sec.getUser().id;
				this.subscriptions.add(
					this.httpClient
						.get(`api/Administration/Users/GetUser/${this.id}`)
						.subscribe((model: EditUserVm) => {
							this.formGroup.patchValue(model);
							this.canSeePasswordButtons =
								model.isActive &&
								!model.isLockedOut &&
								model.userId !== this.sec.getUser().id &&
								model.userId !== 0;
							this.isLockedOut = model.isLockedOut;
							this.origInactivityTimeout = model.inactivityTimeout;
						})
				);

				// I'm both setting the secondary nav in ngOnInit and on nav change
				// since the secondary nav was otherwise not changing when the username
				// in the corner was clicked while on this page
				this.setSecondaryNav();
				this.subscriptions.add(
					this.router.events
						.pipe(filter(event => event instanceof NavigationEnd))
						.subscribe((event: NavigationEnd) => {
							this.setSecondaryNav();
						})
				);
			})
		);
	}

	/** Set the navigation that sits under the main navigation */
	setSecondaryNav() {
		if (this.sec.hasAccess('securityAdministration.editUserDetails')) {
			this.nav.setOpenSecondaryMenu(['Admin', 'Users', 'User List']);
		}
	}

	/** The main save event for the page */
	saveUser(msg: SystemMessage) {
		if (msg.value) {
			const model = this.formGroup.value;
			this.id = model.userId;
			this.canSeePasswordButtons =
				model.isActive && !model.isLockedOut && model.userId !== this.sec.getUser().id && model.userId !== 0;
			if (this.isCurrentUser && this.origInactivityTimeout === false && model.inactivityTimeout === true) {
				this.ms.setSystemMessage('You must reauthenticate to enable the inactivity timeout.', 'success');
				this.sec.setSecurity(null, null, null);
				this.router.navigateByUrl('/login?redirectUrl=' + encodeURIComponent(`/edit-user/${this.id}`));
			} else {
				this.sec.setSecurity(undefined, msg.value.userJsVm, msg.value.dynamicSecurity);
			}
			this.origInactivityTimeout = model.inactivityTimeout;
		}
	}

	/** Unlock user, called when the unlock svg clicked on locked user */
	unlockUser() {
		this.subscriptions.add(
			this.ms.getHttpObservable(this, `api/Administration/Users/UnlockUser/${this.id}`).subscribe(() => {
				this.isLockedOut = false;
			})
		);
	}

	/** Called on click of the reset password and welcome email buttons */
	resetPassword(isWelcomeEmail: boolean) {
		this.subscriptions.add(
			this.httpClient
				.post('api/Account/ForgotPassword', {
					email: this.formGroup.value.email,
					isWelcomeEmail,
				})
				.subscribe((msg: SystemMessage) => {
					if (msg.isSuccessful) {
						this.ms.setSystemMessage(
							`An email has been sent to the user with a ${
								isWelcomeEmail ? 'welcome' : 'reset password'
							} link.`
						);
					} else {
						this.ms.setSystemMessage('An error occurred when attempting to send the email.', 'error');
					}
				})
		);
	}

	/** Opens the change password modal.
	 * Called on click of the Change Passoword button when user looking at their profile. */
	openChangePasswordModal() {
		this.modalService.open(UserChangePasswordModalComponent);
	}

	ngOnDestroy() {
		this.subscriptions.unsubscribe();
	}
}
